import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  CREATE_ENTITY_CONFIG_URL,
  CREATE_MANUAL_VERIFICATION_BINDING,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  GET_LINKED_MANUAL_VERIFICATION_FORMS_API,
  GET_MANUAL_VERIFICATION_BINDINGS,
  GET_MANUAL_VERIFICATION_FORMS,
  REMOVE_MANUAL_VERIFICATION_BINDING,
  get_request_config,
} from '@/utils/system_constants/constant';
import {
  build_binding_payload,
  CADENCE_KIND_OPTIONS,
  format_cadence_kind,
  format_days_from_seconds,
  format_seconds,
  GRACE_PERIOD_OPTIONS,
  get_binding_form_from_binding,
  get_normalized_bindings_from_response,
  get_binding_validation_message,
  get_default_binding_form,
  get_response_data,
  get_response_items,
  is_success_response,
  normalize_manual_form_link,
  normalize_manual_verification_form,
  positive_number_or_null,
  RECURRENCE_INTERVAL_OPTIONS,
  resolve_entity_config_id,
  should_remove_original_binding,
  VERIFICATION_TYPE_OPTIONS,
} from './manual_verification_binding_helpers';

export default {
  name: 'ManualVerificationBinding',
  mixins: [alert_mixin],
  props: {
    entityTypeId: { type: Number, default: null },
    entityTypeName: { type: String, default: '' },
    entityConfigId: { type: Number, default: null },
  },
  data: () => ({
    bindings: [],
    creating_binding: false,
    editing_binding_id: null,
    editing_original_verification_type: '',
    removing_binding_id: null,
    show_bind_modal: false,
    loading_manual_form_links: false,
    resolved_entity_config_id_local: null,
    manual_form_links: [],
    binding_form: get_default_binding_form(),
  }),
  computed: {
    bindings_count() { return Array.isArray(this.bindings) ? this.bindings.length : 0; },
    manual_form_link_options() { return Array.isArray(this.manual_form_links) ? this.manual_form_links : []; },
    verification_type_options() { return VERIFICATION_TYPE_OPTIONS; },
    cadence_kind_options() { return CADENCE_KIND_OPTIONS; },
    recurrence_interval_options() { return RECURRENCE_INTERVAL_OPTIONS; },
    grace_period_options() { return GRACE_PERIOD_OPTIONS; },
    is_periodic() {
      return ['periodic', 'periodic_and_event'].includes(String(this.binding_form?.cadence_kind || ''));
    },
    resolved_entity_config_id() {
      return resolve_entity_config_id(this.entityConfigId, this.resolved_entity_config_id_local, this.$route);
    },
  },
  watch: {
    entityConfigId() {
      this.fetch_bindings();
    },
  },
  mounted() {
    this.fetch_bindings();
  },

  methods: {
    format_seconds,
    format_days_from_seconds,
    format_cadence_kind,
    async fetch_entity_config_by_type() {
      const entity_type_id = positive_number_or_null(this.entityTypeId);
      if (!entity_type_id) return null;

      const config = get_request_config({ params: { entity_type_id } });
      const response = await axios.get(GET_FULL_CONFIG_BY_ENTITY_TYPE_URL, config);
      const config_data = get_response_data(response);
      const entity_config_id = positive_number_or_null(config_data?.entity_config?.id);
      this.resolved_entity_config_id_local = entity_config_id;
      return entity_config_id;
    },
    async ensure_entity_config_id() {
      if (this.resolved_entity_config_id) return this.resolved_entity_config_id;

      const entity_type_id = positive_number_or_null(this.entityTypeId);
      if (!entity_type_id) return null;

      const existing_id = await this.fetch_existing_entity_config_id();
      if (existing_id) return existing_id;

      return this.create_and_fetch_entity_config(entity_type_id);
    },
    async fetch_existing_entity_config_id() {
      const result = await this.fetch_entity_config_by_type()
        .catch((error) => this.get_null_for_missing_config(error));
      return result;
    },
    get_null_for_missing_config(error) {
      if (error?.response?.status !== 404) throw error;
      return null;
    },
    async create_and_fetch_entity_config(entity_type_id) {
      await axios.post(CREATE_ENTITY_CONFIG_URL, { entity_type_id }, get_request_config());
      return this.fetch_entity_config_by_type();
    },

    async fetch_manual_form_links() {
      this.loading_manual_form_links = true;
      try {
        const forms_response = await axios.get(GET_MANUAL_VERIFICATION_FORMS, get_request_config());
        const forms = get_response_items(forms_response)
          .map((form) => normalize_manual_verification_form(form))
          .filter(Boolean);
        const results = await Promise.all(forms.map((form) => axios.get(
          GET_LINKED_MANUAL_VERIFICATION_FORMS_API,
          get_request_config({
            params: { manual_verification_form_id: form.id, only_active: true },
          }),
        ).then((response) => ({ response, form })).catch(() => ({ response: null, form }))));
        this.manual_form_links = results
          .flatMap(({ response, form }) => get_response_items(response)
            .map((link) => normalize_manual_form_link(link, form)))
          .filter((link) => link?.is_active);
      } catch {
        this.manual_form_links = [];
      } finally {
        this.loading_manual_form_links = false;
      }
    },
    async fetch_bindings() {
      const entity_config_id = positive_number_or_null(await this.ensure_entity_config_id());
      if (!entity_config_id) {
        this.clear_bindings();
        return;
      }

      try {
        const response = await this.fetch_binding_response(entity_config_id);
        this.bindings = this.get_normalized_bindings(response);
      } catch {
        this.clear_bindings();
      }
    },
    async fetch_binding_response(entity_config_id) {
      const config = get_request_config({ params: { entity_config_id } });
      const response = await axios.get(GET_MANUAL_VERIFICATION_BINDINGS, config);
      return response;
    },
    get_normalized_bindings(response) { return get_normalized_bindings_from_response(response); },
    clear_bindings() { this.bindings = []; },
    is_success_response(response) { return is_success_response(response); },

    open_bind_modal() {
      this.reset_binding_editor();
      this.binding_form = get_default_binding_form();
      this.show_bind_modal = true;
      this.fetch_manual_form_links();
    },
    open_edit_modal(binding) {
      this.editing_binding_id = positive_number_or_null(binding?.id);
      this.editing_original_verification_type = String(binding?.verification_type || '');
      this.binding_form = get_binding_form_from_binding(binding);
      this.show_bind_modal = true;
      this.fetch_manual_form_links();
    },

    close_bind_modal() { this.show_bind_modal = false; this.reset_binding_editor(); },
    reset_binding_editor() { this.editing_binding_id = null; this.editing_original_verification_type = ''; },

    async submit_binding() {
      if (this.creating_binding) return;
      if (!this.validate_binding_form()) return;

      const entity_config_id = positive_number_or_null(await this.ensure_entity_config_id());
      if (!this.has_entity_config_context(entity_config_id)) return;

      await this.save_binding(entity_config_id);
    },
    validate_binding_form() {
      const validation_message = get_binding_validation_message(this.binding_form);
      if (!validation_message) return true;
      this.show_error(validation_message);
      return false;
    },
    has_entity_config_context(entity_config_id) {
      if (entity_config_id) return true;
      this.show_error('Missing entity configuration context.');
      return false;
    },
    async save_binding(entity_config_id) {
      const payload = build_binding_payload(entity_config_id, this.binding_form, this.is_periodic);
      this.creating_binding = true;
      const was_editing = Boolean(this.editing_binding_id);

      try {
        const response = await this.post_binding_payload(payload);
        this.handle_binding_save_response(response, was_editing);
      } catch {
        this.show_error('Failed to save manual verification binding.');
      } finally {
        this.creating_binding = false;
      }
    },
    async post_binding_payload(payload) {
      await this.remove_original_binding_if_needed(payload.verification_type);
      const response = await axios.post(CREATE_MANUAL_VERIFICATION_BINDING, payload, get_request_config());
      return response;
    },
    handle_binding_save_response(response, was_editing) {
      if (!this.is_success_response(response)) {
        this.show_error(response.data.message);
        return;
      }

      this.show_bind_modal = false;
      this.reset_binding_editor();
      this.fetch_bindings();
      this.show_submit_success(was_editing, response.data.message);
    },

    async remove_original_binding_if_needed(verification_type) {
      const should_remove = should_remove_original_binding(
        this.editing_binding_id,
        this.editing_original_verification_type,
        verification_type,
      );
      if (!should_remove) return;

      const payload = { binding_id: this.editing_binding_id };
      await axios.post(REMOVE_MANUAL_VERIFICATION_BINDING, payload, get_request_config());
    },

    show_submit_success(was_editing, message) {
      const success_message = was_editing ? 'Manual verification binding updated.' : message;
      this.show_success(success_message);
    },

    remove_binding(binding) {
      const binding_id = Number(binding?.id);

      if (this.removing_binding_id === binding_id) return;

      this.show_confirm(
        'Remove manual verification binding?',
        'This will remove the manual verification rule from this entity configuration.',
        () => {
          this.removing_binding_id = binding_id;

          const payload = { binding_id };

          axios.post(REMOVE_MANUAL_VERIFICATION_BINDING, payload, get_request_config())
            .then((response) => {
              if (response.data?.status !== 'success') {
                this.show_error(response.data.message);
                return;
              }

              this.bindings = this.bindings.filter(
                (existing) => Number(existing?.id) !== binding_id
              );
              this.show_success(response.data.message);
            })
            .catch(() => {
              this.show_error('Failed to remove manual verification binding.');
            })
            .finally(() => {
              this.removing_binding_id = null;
            });
        }
      );
    },

    go_to_entity_overview() {
      this.$router.push({
        name: 'EntityConfigDetail',
        params: { entityTypeId: this.entityTypeId },
      });
    },
  },
};


